<template>
    <div v-click-outside="hideSelect"
         :class="['parametric-select__wrapper', {'is-disabled': disabled}]">
        <div class="parametric-select__container">
            <button ref="selectButton"
                    type="button"
                    class="parametric-select__button"
                    :class="{ 'is-active': showOptions, 'is-filled': selectText !== null }"
                    @click.prevent="toggleSelect">
                {{ selectText }}
                <span class="parametric-select__button-icon">
                    <IconDown />
                </span>
            </button>

            <label :for="id"
                   class="parametric-select__label"
                   v-html="label" />

            <Teleport to="#parametric-form">
                <transition name="fadebottom-fast">
                    <div v-show="showOptions"
                         ref="selectList"
                         :style="optionsStyle"
                         class="parametric-select__list-wrapper">
                        <ul class="parametric-select__list">
                            <li v-for="option in innerOptions"
                                :key="option.value"
                                class="parametric-select__list-item">
                                <input :id="`${name}[${option.id}]`"
                                       v-model="option.checked"
                                       type="checkbox"
                                       :name="`${name}[${option.id}]`"
                                       :value="option.value"
                                       :class="{ 'is-disabled': option.disabled }"
                                       @change.prevent="selectItem(option.title, option.id)">
                                <label class="parametric-select__list-item-label"
                                       :for="`${name}[${option.id}]`">
                                    {{ option.title }}
                                    <span class="parametric-select__list-icon">
                                        <IconCheckSmall />
                                    </span>
                                </label>
                            </li>
                        </ul>
                    </div>
                </transition>
            </Teleport>
        </div>
    </div>
</template>

<script>
import ClickOutside from 'vue-click-outside';
import IconCheckSmall from '@/components/Icons/CheckSmall';
import IconDown from '@/components/Icons/Down';
import Teleport from 'vue2-teleport';
import Utils from '@/helpers/Utils';

/**
 * Компонент выпадающего списка
 */
export default {
    name: 'ParametricSelect',

    components: {
        IconCheckSmall,
        IconDown,
        Teleport
    },

    directives: {
        ClickOutside
    },

    props: {
        /**
         * Идентификатор
         */
        id      : String,
        /**
         * Название селекта
         */
        name    : String,
        /**
         * Лэйбл селекта выводимый как placeholder
         */
        label   : String,
        /**
         * Модификатор блока
         */
        modify  : [Array, Object, String],
        /**
         * Является ли обязательным для заполнения
         */
        required: {
            type   : Boolean,
            default: false
        },
        /**
         * Блокирует выбор
         */
        disabled: {
            type   : Boolean,
            default: false
        },
        /**
         * Разрешает множественный выбор в селекте
         */
        multiple: {
            type   : Boolean,
            default: false
        },
        /**
         * Массив опций для селекта
         */
        options: {
            type: Array,
            default() {
                return [];
            }
        },
        /**
         * Текст ошибки
         */
        error: {
            type   : String,
            default: 'Ошибка'
        },
    },

    data() {
        return {
            showOptions : false, // показываем ли опции
            selectValues: [],    // выбранные значения
            optionsStyle: {},    // стили для выпадающего списка,
            innerOptions: [],    // опции которые выводим
        };
    },

    computed: {
        /**
         * Текст выбранных опций в списке
         * @return {string|null}
         */
        selectText() {
            if (!this.selectValues.length) {
                return null;
            }

            let result = '';

            this.selectValues.forEach((item) => {
                result += ', ' + item;
            });

            return result.slice(2);
        }
    },

    watch: {
        /**
         * При изменении опций, устанавливаем выбранные значения
         */
        options: {
            handler() {
                this.setSelectedValues();
                this.$forceUpdate();
            },
            deep: true
        }
    },

    created() {
        // Устанавливаем выбранные значения
        this.setSelectedValues();
    },

    methods: {
        /**
         * Устанавливает выбранные значения
         */
        setSelectedValues() {
            if (this.options?.length) {
                this.innerOptions = this.options;

                this.selectValues = this.options.reduce((result, item) => {
                    if (item.checked) {
                        result.push(item.title);
                    }

                    return result;
                }, []);
            }
        },

        /**
         * Событие при выборе опции
         * @param title
         * @param id
         */
        selectItem(title, id) {
            this.setInputText(title);
            this.changeInnerSelects(id);

            this.$nextTick(() => {
                this.$emit('onChange');
            });
        },

        changeInnerSelects(id) {
            if (this.multiple) {
                return;
            }

            this.innerOptions.forEach((option) => {
                option.checked = option.id === id;
            });
        },

        /**
         * Добаляет/удаляет выбраную опцию из списка выбранных
         * @param {String} title - название опции
         */
        setInputText(title) {
            if (!this.multiple) {
                this.selectValues = [title];

                return;
            }

            if (this.selectValues.length) {
                const index = this.selectValues.findIndex((selected) => selected === title);

                if (index === -1) {
                    this.selectValues.push(title);
                } else {
                    this.selectValues.splice(index, 1);
                }

                return;
            }

            this.selectValues = [title];
        },

        /**
         * Скрывает/показывает список опций
         */
        toggleSelect() {
            this.showOptions = !this.showOptions;

            if (this.showOptions) {
                const rest = this.$refs.selectButton.getBoundingClientRect();

                this.optionsStyle = {
                    position: 'fixed',
                    top     : `${rest.bottom}px`,
                    left    : `${rest.left}px`,
                    width   : `${rest.width}px`
                };

                Utils.bodyFixed(this.$refs.selectList);
            } else {
                Utils.bodyStatic();
            }
        },

        /**
         * Скрывает список опций
         */
        hideSelect(event) {
            if (event.target.closest('.parametric-select__list-wrapper')) {
                return;
            }

            if (this.showOptions) {
                this.showOptions = false;

                Utils.bodyStatic();
            }
        }
    }
};
</script>

<style lang="scss">
@import "ParametricSelect";
</style>
